import { useQuery } from '@tanstack/react-query';
import { getPushMigrationStatus } from '@api-client/api-client';
import { pushMigrationStatusKey } from '@lib/cache/push';

/**
 * How long a fetched migration status stays fresh. The backend flag flips once
 * per environment during the rollout, so this only bounds how quickly an
 * already-open session notices the flip (the default refetch-on-focus re-asks
 * after this window). Not latency-critical: teardown is a once-per-device
 * cleanup, not a user-facing action.
 */
const MIGRATION_STATUS_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * Whether the backend actually sends push via OneSignal
 * (`GET /api/v1/push/migration-status` → ONESIGNAL_PUSH_ENABLED).
 *
 * This is the delivery-readiness half of the legacy-teardown gate: OneSignal's
 * client-side `optedIn` only proves this browser is reachable by OneSignal,
 * not that anything is SENDING through it yet. Tearing down the legacy
 * subscription on opt-in alone would leave a migrated device with zero
 * delivery until the backend flag flips (owner-flag-first rollout order), so
 * `useLegacyWebPushTeardown` must also wait for this signal.
 *
 * Fail-safe by construction: while loading, on error, or when the endpoint is
 * absent (older backend), `data` is undefined → callers read the flag as
 * not-yet-enabled → teardown stays deferred and the device keeps its working
 * legacy path. Duplicates during the overlap are the accepted trade-off;
 * zero delivery is not.
 *
 * Retries: inherits the app QueryClient's `authAwareRetry` default
 * (src/index.tsx), which short-circuits 404s — so a missing endpoint on an
 * older backend costs ONE request per enable cycle, no retry storm, while
 * transient network/5xx failures still get the bounded (≤3) retries that
 * speed teardown recovery. Do not set `retry: false` here; it would diverge
 * from the app-wide policy only to re-implement the 404 half of it.
 *
 * @param enabled - pass false until the value could matter (e.g. before the
 *   device is OneSignal-subscribed and authenticated) so idle sessions never
 *   fire the request.
 */
export function usePushMigrationStatus({ enabled }: { enabled: boolean }) {
  return useQuery({
    queryKey: pushMigrationStatusKey(),
    queryFn: async () => (await getPushMigrationStatus()).data,
    enabled,
    staleTime: MIGRATION_STATUS_STALE_TIME_MS,
  });
}
